import { createTheme } from '@mui/material/styles'

// Dhanshika Exports Brand Colors - Based on reference design
const brandColors = {
  // Primary Brand Colors - Brand Blue
  primary: {
    main: '#2196f3', // Brand blue
    light: '#64b5f6',
    dark: '#0d47a1', // Dark blue for headers
    contrastText: '#ffffff',
  },
  
  // Secondary Brand Colors - Brand Red
  secondary: {
    main: '#f44336', // Brand red
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  
  // Accent Colors
  accent: {
    success: '#4caf50', // Green for success states
    warning: '#ff9800', // Orange for warnings
    error: '#f44336',   // Brand red for errors
    info: '#2196f3',    // Brand blue for information
  },
  
  // Brand Black
  black: '#000000', // Brand black
  
  // Neutral Colors
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // Background Colors
  background: {
    default: '#FAFAFA',
    paper: '#FFFFFF',
    dark: '#121212',
  },
  
  // Text Colors
  text: {
    primary: '#000000', // Brand black
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    hint: 'rgba(0, 0, 0, 0.38)',
  },
}

// Brand Gradients
const brandGradients = {
  primary: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
  secondary: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)',
  hero: 'linear-gradient(135deg, #2196f3 0%, #f44336 100%)',
  card: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
  footer: 'linear-gradient(135deg, #0d47a1 0%, #000000 100%)',
}

// Brand Shadows
const brandShadows = {
  card: '0 2px 8px rgba(0,0,0,0.1)',
  cardHover: '0 4px 16px rgba(0,0,0,0.15)',
  button: '0 2px 4px rgba(0,0,0,0.1)',
  buttonHover: '0 4px 8px rgba(0,0,0,0.15)',
  header: '0 2px 4px rgba(0,0,0,0.1)',
}

// Dhanshika Exports B2B Admin Theme
export const theme = createTheme({
  palette: {
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    success: {
      main: brandColors.accent.success,
      light: '#81c784',
      dark: '#388e3c',
    },
    warning: {
      main: brandColors.accent.warning,
      light: '#ffb74d',
      dark: '#f57c00',
    },
    error: {
      main: brandColors.accent.error,
      light: brandColors.secondary.light,
      dark: brandColors.secondary.dark,
    },
    info: {
      main: brandColors.accent.info,
      light: brandColors.primary.light,
      dark: brandColors.primary.dark,
    },
    background: brandColors.background,
    text: brandColors.text,
    grey: brandColors.neutral,
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.5,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
    },
    subtitle2: {
      fontWeight: 500,
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.75rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: brandShadows.button,
          '&:hover': {
            boxShadow: brandShadows.buttonHover,
          },
        },
        outlined: {
          borderWidth: '1px',
          '&:hover': {
            borderWidth: '1px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: brandShadows.card,
          borderRadius: 12,
          '&:hover': {
            boxShadow: brandShadows.cardHover,
          },
          transition: 'box-shadow 0.2s ease-in-out',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: brandShadows.header,
          background: brandGradients.primary,
          color: '#ffffff',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(33, 150, 243, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.12)',
            },
          },
        },
      },
    },
    // MuiDataGrid will be configured when @mui/x-data-grid is properly installed
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.primary.main,
          color: brandColors.primary.contrastText,
          fontWeight: 600,
        },
      },
    },
  },
})

export default theme

// Export brand utilities for use in components
export { brandColors, brandGradients, brandShadows }
